import { WintSuperkollAPIModelWintSalaryReport } from "wint-api-client/dist/types";

type PayoutRecord = WintSuperkollAPIModelWintSalaryReport.PayoutRecord;
const { SalaryPaymentState } = WintSuperkollAPIModelWintSalaryReport;

/**
 * `NotPaid` identifies the synthetic remainder, never a real payout request: Wint.Salary's
 * `PayoutRecord` contract states that every other state corresponds to a persisted request, and
 * its `MapPayoutRequestState` only ever emits PaymentInProcess/SentToBank/Paid. It is therefore
 * the one entry with no request behind it, so its date is still open.
 */
const isSyntheticRemainder = (payout: PayoutRecord) =>
  payout.paymentState === SalaryPaymentState.NotPaid;

/** One rendered payout line: an amount, whether it is already settled, and the date to label it with. */
export interface PayoutLine {
  settled: boolean;
  /** Money no payout request covers yet, which approving now is what pays out. */
  unscheduled: boolean;
  date?: Date;
  amount: number;
}

/**
 * Breaks a row's regular-payday total into one line per (settled, date) group, chronologically.
 * Normally yields a single line — two only while a month is part settled, part outstanding.
 *
 * A payout request carries its own date; the synthetic remainder is not a request yet, so
 * approving now is what decides when it lands — that is the only line `unscheduledPayoutDate`
 * ("if approved today") belongs on. Labelling every line with it is what made a settled payout
 * read as "Betalas ut {future date}" (DEV-28550).
 *
 * `payouts` is only populated by report endpoints that carry a payday, so an empty list falls back
 * to one undifferentiated line, keeping the previews and the deviation report rendering as before.
 */
export const buildPayoutLines = (
  payouts: PayoutRecord[] | undefined,
  regularPayoutAmount: number,
  unscheduledPayoutDate: Date | undefined
): PayoutLine[] => {
  const regular = (payouts ?? []).filter((payout) => !payout.isEarlyPayout);
  if (regular.length === 0) {
    return [{ settled: false, unscheduled: true, date: unscheduledPayoutDate, amount: regularPayoutAmount }];
  }

  const lines: PayoutLine[] = [];
  for (const payout of regular) {
    const settled = payout.paymentState === SalaryPaymentState.Paid;
    const unscheduled = isSyntheticRemainder(payout);
    const date = unscheduled
      ? unscheduledPayoutDate
      : // Dates arrive as ISO strings at runtime despite the generated Date type.
        new Date(payout.payoutDate);
    const existing = lines.find(
      (line) =>
        line.settled === settled && line.unscheduled === unscheduled && line.date?.getTime() === date?.getTime()
    );
    if (existing) {
      existing.amount += payout.amount;
    } else {
      lines.push({ settled, unscheduled, date, amount: payout.amount });
    }
  }

  // Order by the date actually shown, not the incoming order: the remainder's displayed date is
  // the "if approved today" one, which can fall after a scheduled payout it sorts before, so
  // relying on Core's PayoutDate ordering renders a later date above an earlier one.
  return lines.sort((a, b) => (a.date?.getTime() ?? Infinity) - (b.date?.getTime() ?? Infinity));
};

/**
 * The part of a row's regular-payday total that approving the month actually releases.
 *
 * `regularPayoutAmount` is a total, so it counts payouts that already exist as requests. An
 * approval releases only the synthetic remainder: money already sent for payment is released
 * whether or not the bank has confirmed it, so paid and in-flight requests are both excluded.
 * This is a different question from the "Utbetald" label above, which must stay confirmed-only.
 *
 * Subtracts from the authoritative total rather than summing the remainder — identical while the
 * records reconcile with the total, but an incomplete record list then cannot understate what the
 * approval releases. Falls back to the total when the row carries no records.
 */
export const outstandingRegularAmount = (
  payouts: PayoutRecord[] | undefined,
  regularPayoutAmount: number
): number => {
  if (!payouts?.length) {
    return regularPayoutAmount;
  }
  const alreadyRequested = payouts
    .filter((payout) => !payout.isEarlyPayout && !isSyntheticRemainder(payout))
    .reduce((sum, payout) => sum + payout.amount, 0);
  return regularPayoutAmount - alreadyRequested;
};
